import { useEffect, useState, useCallback } from "react";
import { Loader2, RefreshCw, Star, Package, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { OVERLINE, H1, btnPrimary, Panel, useAdmin } from "@/components/admin/ui";

const fmt = (c) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((c || 0) / 100);

export default function AdminMerch() {
  const { admin } = useAdmin();
  const canEdit = admin?.permissions?.includes("marketplace");
  const [status, setStatus] = useState(null);
  const [products, setProducts] = useState(null);
  const [orders, setOrders] = useState([]);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(() => {
    api.adminMerchStatus().then(setStatus).catch(() => setStatus({ configured: false }));
    api.adminMerchProducts().then((d) => setProducts(d.products)).catch(() => setProducts([]));
    api.adminMerchOrders().then(setOrders).catch(() => setOrders([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try { await api.adminMerchSync(); toast.success("Catalog synced"); load(); }
    catch { toast.error("Sync failed"); } finally { setSyncing(false); }
  };
  const toggleFeature = async (p) => {
    try { await api.adminMerchFeature(p.id, !p.featured); setProducts((rows) => rows.map((x) => (x.id === p.id ? { ...x, featured: !p.featured } : x))); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="p-6 md:p-8" data-testid="admin-merch">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><span className={OVERLINE}>Commerce</span><h1 className={`${H1} mt-1`}>Merch (Printify)</h1></div>
        {canEdit && <button onClick={sync} disabled={syncing} className={btnPrimary} data-testid="merch-sync-btn">{syncing ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Sync catalog</button>}
      </div>

      {status === null ? (
        <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-[#FF6A00]" /></div>
      ) : !status.configured ? (
        <p className="text-[#8A5A44] py-8">Printify isn't configured yet. Add the PRINTIFY_API_TOKEN secret to connect your shop.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5" data-testid="merch-status">
            <div className="bg-white border border-[#E5E2DC] p-4"><p className="text-xs text-[#8A8478]">Shop ID</p><p className="text-lg font-semibold text-[#1F2A22]">{status.shop_id}</p></div>
            <div className="bg-white border border-[#E5E2DC] p-4"><p className="text-xs text-[#8A8478]">Products</p><p className="text-lg font-semibold text-[#1F2A22]">{status.product_count}</p></div>
            <div className="bg-white border border-[#E5E2DC] p-4"><p className="text-xs text-[#8A8478]">Visible</p><p className="text-lg font-semibold text-[#1F2A22]">{status.visible_count}</p></div>
            <div className="bg-white border border-[#E5E2DC] p-4"><p className="text-xs text-[#8A8478]">Last sync</p><p className="text-sm font-medium text-[#1F2A22]">{status.synced_at ? new Date(status.synced_at).toLocaleString() : "—"}</p></div>
          </div>

          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 mt-6">
            <Panel title="Products — choose what's featured on the homepage">
              {products === null ? <Loader2 className="animate-spin text-[#FF6A00]" /> : (
                <div className="divide-y divide-[#F0EEE9]" data-testid="merch-products">
                  {products.map((p) => (
                    <div key={p.id} className="flex items-center gap-3 py-2.5" data-testid={`merch-product-${p.id}`}>
                      {p.images?.[0] && <img src={p.images[0]} alt="" className="w-11 h-11 object-cover border border-[#1F2A22]/10 shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[#1F2A22] truncate">{p.title}</p>
                        <p className="text-xs text-[#8A8478]">{p.price_from != null ? `From ${fmt(p.price_from)}` : "—"} · {p.variant_count} variants{!p.visible && " · hidden"}</p>
                      </div>
                      <button onClick={() => toggleFeature(p)} disabled={!canEdit} className={`inline-flex items-center gap-1 text-xs border px-2 py-1 shrink-0 ${p.featured ? "border-[#FF6A00] text-[#FF6A00] bg-[#FF6A00]/5" : "border-[#E5E2DC] text-[#5C6B61]"} disabled:opacity-40`} data-testid={`merch-feature-${p.id}`}>
                        <Star size={13} className={p.featured ? "fill-[#FF6A00]" : ""} /> {p.featured ? "Featured" : "Feature"}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Panel>

            <Panel title="Recent orders">
              {orders.length === 0 ? (
                <p className="text-sm text-[#8A8478] flex items-center gap-2"><Package size={15} /> No orders yet.</p>
              ) : (
                <div className="space-y-2" data-testid="merch-orders">
                  {orders.map((o) => (
                    <div key={o.id} className="border border-[#E5E2DC] p-3" data-testid={`merch-order-${o.id}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-[#1F2A22]">{fmt(o.total_cents)}</span>
                        <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 ${o.status === "submitted" ? "bg-[#2E7D46]/15 text-[#2E7D46]" : o.status === "error" ? "bg-red-100 text-red-700" : "bg-[#8A5A44]/15 text-[#8A5A44]"}`}>
                          {o.status === "error" ? <AlertCircle size={11} /> : <CheckCircle2 size={11} />} {o.status.replace("_", " ")}{o.mode === "test" ? " · test" : ""}
                        </span>
                      </div>
                      <p className="text-xs text-[#8A8478] mt-1">{o.email} · {(o.items_summary || []).length} item(s){o.printify_order_id ? ` · Printify #${String(o.printify_order_id).slice(0, 8)}` : ""}</p>
                    </div>
                  ))}
                </div>
              )}
            </Panel>
          </div>
        </>
      )}
    </div>
  );
}
